import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Slide,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Step,
  Stepper,
  StepLabel
} from "@material-ui/core";

import axios from "axios";
import envConfig from "../../../bin/env.config";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import ImportCSVHistoryList from "./ImportCSVHistoryList";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

export default function DialogImportCSV({open, onClose, project_id, reloadData}) {
  const token = useSelector((state) => state.session.token);
  
  // State management
  const [currentStep, setCurrentStep] = useState(0);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({
    name: '',
    keyword: '',
    type: '',
    category: '',
    sub_category: ''
  });
  const [importing, setImporting] = useState(false);
  const [importLogs, setImportLogs] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [uniqueCode, setUniqueCode] = useState(null);

  const steps = ['Select CSV File', 'Map Columns', 'Import Data'];

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setCsvFile(null);
      setCsvData([]);
      setCsvHeaders([]);
      setColumnMapping({
        name: '',
        keyword: '',
        type: '',
        category: '',
        sub_category: ''
      });
      setImporting(false);
      setImportLogs([]);
      setImportProgress(0);
      setUniqueCode(uuidv4());
    }
  }, [open]);

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      parseCSVFile(file);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  // Parse CSV file
  const parseCSVFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length > 0) {
        const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
        const data = lines.slice(1).map(line => {
          const values = line.split(',').map(value => {
            const cleaned = value.trim().replace(/"/g, '');
            // Convert various empty representations to null
            if (cleaned === '' || cleaned === 'null' || cleaned === 'NULL' || cleaned === 'Null') {
              return null;
            }
            return cleaned;
          });
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || null;
          });
          return row;
        });
        
        setCsvHeaders(headers);
        setCsvData(data);
        setCurrentStep(1);
      }
    };
    reader.readAsText(file);
  };

  // Handle column mapping change
  const handleColumnMappingChange = (field, value) => {
    setColumnMapping({
      ...columnMapping,
      [field]: value
    });
  };

  // Proceed to import step
  const handleProceedToImport = () => {
    // Validate that name field is mapped
    if (!columnMapping.name || columnMapping.name.trim() === '') {
      alert('Please map the "name" field before proceeding with import. Product name is required.');
      return;
    }
    
    // Pre-validate and show summary
    const validRows = [];
    const invalidRows = [];
    
    csvData.forEach((row, index) => {
      const rawName = row[columnMapping.name];
      const trimmedName = (rawName || '').toString().trim();
      
      if (!rawName || rawName === null || rawName === undefined || 
          rawName === '' || rawName === 'null' || rawName === 'NULL' || 
          trimmedName === '' || trimmedName === 'null' || trimmedName === 'NULL') {
        invalidRows.push({ row: index + 1, reason: 'Empty or null name' });
      } else {
        validRows.push({ row: index + 1, name: trimmedName });
      }
    });
    
    const message = `Import Summary:
                    - Total rows: ${csvData.length}
                    - Valid rows: ${validRows.length}
                    - Invalid rows (will be skipped): ${invalidRows.length}

${invalidRows.length > 0 ? 'Invalid rows:\n' + invalidRows.slice(0, 5).map(r => `Row ${r.row}: ${r.reason}`).join('\n') + (invalidRows.length > 5 ? `\n... and ${invalidRows.length - 5} more` : '') : ''}

Do you want to proceed with the import?`;
    
    if (!window.confirm(message)) {
      return;
    }
    
    setCurrentStep(2);
    startImport();
  };

  // Create initial log entry
  const createImportLog = async (successCount, errorCount) => {
    try {
      const logData = {
        code: uniqueCode,
        project_id: project_id,
        file_name: csvFile.name,
        total_row: csvData.length,
        total_success: successCount,
        total_error: errorCount,
      };

      console.log('Creating import log with data:', logData);

      const url = `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/project/import-csv/log-insert`;
      await axios.post(url, logData, {
        headers: {
          Authorization: JSON.stringify(token),
        },
      });
    } catch (error) {
      console.error('Error creating import log:', error);
      
      return null; 
    }
  };

  

 

  // Start import process
  const startImport = async () => {
    setImporting(true);
    setImportLogs([]);
    setImportProgress(0);

    // Create initial log entry and get the logId
    
    const totalRows = csvData.length;
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < csvData.length; i++) {
      const row = csvData[i];
      
      try {
        // Map CSV columns to API fields
        const productData = {
          project_id: project_id,          
          name: row[columnMapping.name] || '',
          key_words: row[columnMapping.keyword] || '',
          type: row[columnMapping.type] || '',
          category: row[columnMapping.category] || '',
          sub_category: row[columnMapping.sub_category] || '',
          brand_name: '', // Default empty if not mapped
          description: 'Imported from CSV code:'+uniqueCode, // Default empty if not mapped
          sort_order: 0 // Default value
        };

        // Enhanced validation with detailed logging
        const rawName = row[columnMapping.name];
        const trimmedName = productData.name.trim();
        
        // Log the raw data for debugging
        setImportLogs(prev => [...prev, {
          type: 'info',
          message: `Row ${i + 1}: Raw name value: "${rawName}" (type: ${typeof rawName})`
        }]);

        // Validate required fields before API call
        if (!rawName || rawName === null || rawName === undefined || 
            rawName === '' || rawName === 'null' || rawName === 'NULL' || 
            trimmedName === '' || trimmedName === 'null' || trimmedName === 'NULL') {
          errorCount++;
          setImportLogs(prev => [...prev, {
            type: 'error',
            message: `Row ${i + 1}: Skipped - Product name is required but was empty or null. Raw value: "${rawName}"`
          }]);
          continue; // Skip this row and continue with next
        }

        // Call API to insert product
        const url = `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/update-product`;
        await axios.post(url, productData, {
          headers: {
            Authorization: JSON.stringify(token),
          },
        });

        successCount++;
        setImportLogs(prev => [...prev, {
          type: 'success',
          message: `Row ${i + 1}: Successfully imported "${productData.name}"`
        }]);

      } catch (error) {
        errorCount++;
        setImportLogs(prev => [...prev, {
          type: 'error',
          message: `Row ${i + 1}: Error importing "${row[columnMapping.name] || 'Unknown'}": ${error.response?.data?.message || error.message}`
        }]);
      }

      // Update progress
      setImportProgress(((i + 1) / totalRows) * 100);
    }

    setImporting(false);
    
    
    // Update log with final statistics using the local logId
    await createImportLog(successCount, errorCount);
    

    // Reload data if there were successful imports
    // if (successCount > 0) {
    //   reloadData();
    // }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!importing) {
      onClose();
    }
  };
  const handleCloseFinish = () => {
    if (!importing) {
      // onClose();
      if (importLogs.length > 0) {
        reloadData();
      }
      onClose();
    }
  };
  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 1: Select CSV File
            </Typography>
            <DialogContentText>
              Please select a CSV file to import. The file should contain columns that can be mapped to product fields.
            </DialogContentText>
            <Box mt={2}>
              <input
                accept=".csv"
                style={{ display: 'none' }}
                id="csv-file-input"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="csv-file-input">
                <Button variant="contained" component="span">
                  Select CSV File
                </Button>
              </label>
              {/* Import CSV history */}
              <ImportCSVHistoryList project_id={project_id} />
            </Box>
            {csvFile && (
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  Selected file: {csvFile.name}
                </Typography>
              </Box>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 2: Map Columns
            </Typography>
            <DialogContentText>
              Map the CSV columns to the corresponding product fields. Only mapped fields will be imported.
              <br />
              <strong>Note: The "name" field is required and must be mapped to proceed with import.</strong>
            </DialogContentText>
            <Box mt={2}>
              {Object.keys(columnMapping).map((field) => (
                <FormControl key={field} fullWidth margin="normal" required={field === 'name'}>
                  <InputLabel required={field === 'name'}>
                    {field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                    {field === 'name' && ' *'}
                  </InputLabel>
                  <Select
                    value={columnMapping[field]}
                    onChange={(e) => handleColumnMappingChange(field, e.target.value)}
                    error={field === 'name' && !columnMapping.name}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {csvHeaders.map((header) => (
                      <MenuItem key={header} value={header}>
                        {header}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
            </Box>
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                Preview (first 3 rows):
              </Typography>
              <Paper style={{ maxHeight: 200, overflow: 'auto', marginTop: 8 }}>
                <List dense>
                  {csvData.slice(0, 3).map((row, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={`Row ${index + 1}`}
                        secondary={Object.entries(columnMapping)
                          .filter(([_, value]) => value)
                          .map(([field, column]) => `${field}: ${row[column] || 'N/A'}`)
                          .join(', ')}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 3: Import Progress
            </Typography>
            {importing && (
              <Box mb={2}>
                <LinearProgress variant="determinate" value={importProgress} />
                <Typography variant="body2" color="textSecondary" align="center">
                  {Math.round(importProgress)}% Complete
                </Typography>
              </Box>
            )}
            <Paper style={{ maxHeight: 300, overflow: 'auto' }}>
              <List dense>
                {importLogs.map((log, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={log.message}
                      style={{
                        color: log.type === 'error' ? 'red' : 
                               log.type === 'success' ? 'green' : 'inherit'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
      <Dialog
        open={open}
      onClose={handleClose}
        TransitionComponent={Transition}
      aria-labelledby="Import CSV"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Import Products from CSV
        <Stepper activeStep={currentStep} style={{ marginTop: 16 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>
        <DialogContent>
        {renderStepContent()}
        </DialogContent>
        <DialogActions>
        {currentStep === 0 && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
          </>
        )}
        {currentStep === 1 && (
          <>
            <Button onClick={() => setCurrentStep(0)}>Back</Button>
            <Button 
              onClick={handleProceedToImport}
              variant="contained"
              color="primary"
            >
              Start Import
            </Button>
          </>
        )}
        {currentStep === 2 && (
          <>
            <Button 
              onClick={handleCloseFinish}
              disabled={importing}
              variant="contained"
            >
              {importing ? 'Importing...' : 'Close'}
            </Button>
          </>
        )}
        </DialogActions>
      </Dialog>
  );
}
